import { useState } from 'react';
import { ArrowLeft, Search, Sparkles, Star, Heart, MapPin, Briefcase, GraduationCap, Calendar, Video, Camera, TrendingUp, Zap, Filter, Check, X, Users, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useTheme, themes } from './ThemeProvider';
import { toast } from 'sonner';

interface AdvancedMatchingPageProps {
  onBack: () => void;
}

interface AdvancedProfile {
  id: number;
  name: string;
  age: number;
  location: string;
  profession: string;
  education: string;
  image: string;
  verified: boolean;
  premium: boolean;
  horoscope: string;
  zodiacSign: string;
  photoCount: number;
  hasVideo: boolean;
  responseRate: number;
  lastActive: string;
  salary: string;
  height: string;
  interests: string[];
  lifestyle: {
    smoking: string;
    drinking: string;
    diet: string;
    pets: string;
    fitness: string;
  };
  family: {
    familyType: string;
    familyValues: string;
    fatherOccupation: string;
    motherOccupation: string;
  };
  languages: string[];
  socialMediaVerified: boolean;
  educationTier: string;
  careerStability: number;
  travelFrequency: string;
  matchScore: number;
}

const sampleProfiles: AdvancedProfile[] = [
  {
    id: 1,
    name: "Priya Sharma",
    age: 26,
    location: "Mumbai, Maharashtra",
    profession: "Marketing Manager",
    education: "MBA - IIM Ahmedabad",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=500&fit=crop",
    verified: true,
    premium: true,
    horoscope: "Kumbh",
    zodiacSign: "Aquarius",
    photoCount: 8,
    hasVideo: true,
    responseRate: 95,
    lastActive: "Online now",
    salary: "₹8-10 LPA",
    height: "5'5\"",
    interests: ["Travel", "Reading", "Yoga", "Music"],
    lifestyle: {
      smoking: "No",
      drinking: "No",
      diet: "Vegetarian",
      pets: "Loves Dogs",
      fitness: "Regular Gym"
    },
    family: {
      familyType: "Nuclear Family",
      familyValues: "Traditional",
      fatherOccupation: "Business",
      motherOccupation: "Homemaker"
    },
    languages: ["Hindi", "English", "Gujarati"],
    socialMediaVerified: true,
    educationTier: "Tier 1",
    careerStability: 90,
    travelFrequency: "Monthly",
    matchScore: 95
  },
  {
    id: 2,
    name: "Amit Patel",
    age: 29,
    location: "Bangalore, Karnataka",
    profession: "Software Engineer",
    education: "B.Tech - IIT Delhi",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=500&fit=crop",
    verified: true,
    premium: false,
    horoscope: "Vrishabha",
    zodiacSign: "Taurus",
    photoCount: 6,
    hasVideo: false,
    responseRate: 82,
    lastActive: "Active 2 hours ago",
    salary: "₹15-20 LPA",
    height: "5'10\"",
    interests: ["Technology", "Cricket", "Travel", "Photography"],
    lifestyle: {
      smoking: "No",
      drinking: "Occasionally",
      diet: "Vegetarian",
      pets: "No Preference",
      fitness: "Yoga"
    },
    family: {
      familyType: "Joint Family",
      familyValues: "Moderate",
      fatherOccupation: "Engineer",
      motherOccupation: "Teacher"
    },
    languages: ["Gujarati", "English", "Hindi"],
    socialMediaVerified: false,
    educationTier: "Tier 1",
    careerStability: 95,
    travelFrequency: "Quarterly",
    matchScore: 88
  }
];

export default function AdvancedMatchingPage({ onBack }: AdvancedMatchingPageProps) {
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  // Filter states
  const [ageRange, setAgeRange] = useState([22, 35]);
  const [heightRange, setHeightRange] = useState([150, 185]);
  const [selectedHoroscope, setSelectedHoroscope] = useState<string>('any');
  const [selectedEducationTier, setSelectedEducationTier] = useState<string>('any');
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [minPhotoCount, setMinPhotoCount] = useState(0);
  const [videoProfileOnly, setVideoProfileOnly] = useState(false);
  const [minResponseRate, setMinResponseRate] = useState(0);
  const [lastActiveFilter, setLastActiveFilter] = useState<string>('any');
  const [socialVerifiedOnly, setSocialVerifiedOnly] = useState(false);
  const [minCareerStability, setMinCareerStability] = useState(0);
  const [selectedPets, setSelectedPets] = useState<string>('any');
  const [selectedFitness, setSelectedFitness] = useState<string>('any');
  const [selectedFamilyValues, setSelectedFamilyValues] = useState<string>('any');
  const [selectedTravelFreq, setSelectedTravelFreq] = useState<string>('any');
  
  const [results, setResults] = useState<AdvancedProfile[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const horoscopes = [
    'Mesh (Aries)', 'Vrishabha (Taurus)', 'Mithun (Gemini)', 'Karka (Cancer)',
    'Simha (Leo)', 'Kanya (Virgo)', 'Tula (Libra)', 'Vrishchik (Scorpio)',
    'Dhanu (Sagittarius)', 'Makar (Capricorn)', 'Kumbh (Aquarius)', 'Meen (Pisces)'
  ];

  const languages = ['Hindi', 'English', 'Gujarati', 'Marathi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Punjabi'];

  const handleSearch = async () => {
    try {
      setIsSearching(true);
      
      // Build filter object
      const filters: any = {
        page: 1,
        limit: 20
      };

      // Age filters
      if (ageRange[0] !== 18 || ageRange[1] !== 60) {
        filters.minAge = ageRange[0];
        filters.maxAge = ageRange[1];
      }

      // Height filters (convert cm to height)
      if (heightRange[0] !== 140 || heightRange[1] !== 200) {
        filters.minHeight = heightRange[0];
        filters.maxHeight = heightRange[1];
      }

      // Horoscope filter
      if (selectedHoroscope !== 'any') {
        filters.horoscope = selectedHoroscope;
      }

      // Education tier
      if (selectedEducationTier !== 'any') {
        filters.educationTier = selectedEducationTier;
      }

      // Languages
      if (selectedLanguages.length > 0) {
        filters.languages = selectedLanguages;
      }

      // Profile quality filters
      if (minPhotoCount > 0) {
        filters.minPhotoCount = minPhotoCount;
      }

      if (videoProfileOnly) {
        filters.videoProfileOnly = true;
      }

      if (socialVerifiedOnly) {
        filters.photoVerified = true;
      }

      // Lifestyle filters
      if (selectedPets !== 'any') {
        filters.pets = selectedPets;
      }

      if (selectedFitness !== 'any') {
        filters.fitness = selectedFitness;
      }

      if (selectedFamilyValues !== 'any') {
        filters.familyValues = selectedFamilyValues;
      }

      if (selectedTravelFreq !== 'any') {
        filters.travelFrequency = selectedTravelFreq;
      }

      // Call production API
      const response = await apiClient.searchProfiles(filters);
      
      if (response.success && response.data?.profiles) {
        // Transform API profiles to match our interface
        const transformedProfiles = response.data.profiles.map((profile: any, index: number) => ({
          id: index + 1,
          name: `${profile.userId?.firstName || 'User'} ${profile.userId?.lastName || ''}`.trim(),
          age: profile.dateOfBirth ? calculateAge(profile.dateOfBirth) : 25,
          location: `${profile.location?.city || ''}, ${profile.location?.state || ''}`.trim() || 'India',
          profession: profile.career?.occupation || 'Professional',
          education: profile.education?.highestDegree || 'Graduate',
          image: profile.profilePhoto || profile.photos?.[0]?.url || '', // Empty string instead of Unsplash
          verified: profile.userId?.isPhoneVerified || false,
          premium: profile.userId?.subscription?.isActive || false,
          horoscope: profile.astrology?.horoscope || 'Not specified',
          zodiacSign: profile.astrology?.zodiacSign || '',
          photoCount: profile.photos?.length || 0,
          hasVideo: profile.hasVideoProfile || false,
          responseRate: 85,
          lastActive: 'Recently active',
          salary: profile.career?.annualIncome ? `₹${profile.career.annualIncome} LPA` : 'Not specified',
          height: profile.height ? `${profile.height} cm` : '165 cm',
          interests: [],
          lifestyle: {
            smoking: profile.lifestyle?.smoking || 'No',
            drinking: profile.lifestyle?.drinking || 'No',
            diet: profile.lifestyle?.diet || 'Vegetarian',
            pets: profile.lifestyle?.pets || 'No preference',
            fitness: profile.lifestyle?.fitness || 'Active'
          },
          family: {
            familyType: profile.family?.familyType || 'Nuclear',
            familyValues: profile.family?.familyValues || 'Moderate',
            fatherOccupation: profile.family?.fatherOccupation || '',
            motherOccupation: profile.family?.motherOccupation || ''
          },
          languages: profile.languages || [],
          socialMediaVerified: profile.isSocialMediaVerified || false,
          educationTier: profile.education?.institutionTier || 'Tier 2',
          careerStability: 85,
          travelFrequency: profile.lifestyle?.travelFrequency || 'Quarterly',
          matchScore: 88
        }));

        setResults(transformedProfiles);
        toast.success(`Found ${transformedProfiles.length} matches based on your criteria`);
      } else {
        // Fallback to sample profiles if API fails
        setResults(sampleProfiles);
        toast.info('Showing sample matches. Connect backend for real profiles.');
      }
    } catch (error: any) {
      // Silent fail
      // console.error('Search error:', error);
      // Show sample profiles on error
      setResults(sampleProfiles);
      toast.info('Showing sample matches. Connect backend for real profiles.');
    } finally {
      setIsSearching(false);
    }
  };

  // Helper function to calculate age from date of birth
  const calculateAge = (dateOfBirth: string): number => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const clearAllFilters = () => {
    setAgeRange([22, 35]);
    setHeightRange([150, 185]);
    setSelectedHoroscope('any');
    setSelectedEducationTier('any');
    setSelectedLanguages([]);
    setMinPhotoCount(0);
    setVideoProfileOnly(false);
    setMinResponseRate(0);
    setLastActiveFilter('any');
    setSocialVerifiedOnly(false);
    setMinCareerStability(0);
    setSelectedPets('any');
    setSelectedFitness('any');
    setSelectedFamilyValues('any');
    setSelectedTravelFreq('any');
    setResults([]);
    toast.info('All filters cleared');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className={`bg-gradient-to-r ${currentTheme.primary} text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-40 shadow-md`}>
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={onBack}>
          <ArrowLeft className="size-6" />
        </Button>
        <div className="flex-1">
          <span className="text-lg font-medium">Advanced Match Finder</span>
          <p className="text-xs text-white/80">Find your perfect match with precision filters</p>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="filters" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-flex">
              <TabsTrigger value="filters">
                <Filter className="size-4 mr-2" />
                Smart Filters
              </TabsTrigger>
              <TabsTrigger value="results" disabled={results.length === 0}>
                <Users className="size-4 mr-2" />
                Matches ({results.length})
              </TabsTrigger>
            </TabsList>

            {/* Filters Tab */}
            <TabsContent value="filters" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {/* Basic Filters */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Search className="size-5" />
                      Basic Filters
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Age Range: {ageRange[0]} - {ageRange[1]} years</Label>
                      <Slider
                        min={18}
                        max={60}
                        step={1}
                        value={ageRange}
                        onValueChange={setAgeRange}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Height: {heightRange[0]} - {heightRange[1]} cm</Label>
                      <Slider
                        min={140}
                        max={200}
                        step={1}
                        value={heightRange}
                        onValueChange={setHeightRange}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Education Institution Tier</Label>
                      <Select value={selectedEducationTier} onValueChange={setSelectedEducationTier}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="Tier 1">Tier 1 (IIT/IIM/AIIMS)</SelectItem>
                          <SelectItem value="Tier 2">Tier 2 (NIT/State Universities)</SelectItem>
                          <SelectItem value="Tier 3">Tier 3 (Other Colleges)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Astrological Compatibility */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="size-5" />
                      Astrological Match
                    </CardTitle>
                    <CardDescription>Find horoscope-compatible matches</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Preferred Horoscope</Label>
                      <Select value={selectedHoroscope} onValueChange={setSelectedHoroscope}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any Horoscope</SelectItem>
                          {horoscopes.map((sign) => (
                            <SelectItem key={sign} value={sign}>
                              {sign}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="size-4 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-900">AI Astro Match</span>
                      </div>
                      <p className="text-xs text-purple-700">
                        We'll show you astrologically compatible profiles based on Vedic principles
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Profile Quality Indicators */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="size-5" />
                      Profile Quality
                    </CardTitle>
                    <CardDescription>Filter by profile completeness</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Minimum Photos: {minPhotoCount}</Label>
                      <Slider
                        min={0}
                        max={10}
                        step={1}
                        value={[minPhotoCount]}
                        onValueChange={([value]) => setMinPhotoCount(value)}
                      />
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="video" 
                          checked={videoProfileOnly}
                          onCheckedChange={(checked) => setVideoProfileOnly(checked as boolean)}
                        />
                        <Label htmlFor="video" className="flex items-center gap-2">
                          <Video className="size-4 text-rose-600" />
                          Video Profile Available
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="socialVerified" 
                          checked={socialVerifiedOnly}
                          onCheckedChange={(checked) => setSocialVerifiedOnly(checked as boolean)}
                        />
                        <Label htmlFor="socialVerified" className="flex items-center gap-2">
                          <Globe className="size-4 text-blue-600" />
                          Social Media Verified
                        </Label>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Activity & Engagement */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="size-5" />
                      Activity & Response
                    </CardTitle>
                    <CardDescription>Filter by user engagement</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Last Active</Label>
                      <Select value={lastActiveFilter} onValueChange={setLastActiveFilter}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any time</SelectItem>
                          <SelectItem value="online">Online now</SelectItem>
                          <SelectItem value="today">Active today</SelectItem>
                          <SelectItem value="week">Active this week</SelectItem>
                          <SelectItem value="month">Active this month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Minimum Response Rate: {minResponseRate}%</Label>
                      <Slider
                        min={0}
                        max={100}
                        step={10}
                        value={[minResponseRate]}
                        onValueChange={([value]) => setMinResponseRate(value)}
                      />
                      <p className="text-xs text-gray-500">Higher response rate means they reply quickly</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Career Stability */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="size-5" />
                      Career & Stability
                    </CardTitle>
                    <CardDescription>Professional background filters</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Career Stability Score: {minCareerStability}%</Label>
                      <Slider
                        min={0}
                        max={100}
                        step={10}
                        value={[minCareerStability]}
                        onValueChange={([value]) => setMinCareerStability(value)}
                      />
                      <p className="text-xs text-gray-500">Based on job tenure, industry, and growth</p>
                    </div>

                    <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Zap className="size-4 text-amber-600" />
                        <span className="text-sm font-semibold text-amber-900">Premium Feature</span>
                      </div>
                      <p className="text-xs text-amber-700">
                        Career stability scores available for Premium members
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Lifestyle Compatibility */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="size-5" />
                      Lifestyle Match
                    </CardTitle>
                    <CardDescription>Find compatible lifestyles</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Pet Preference</Label>
                      <Select value={selectedPets} onValueChange={setSelectedPets}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="Loves Dogs">Loves Dogs</SelectItem>
                          <SelectItem value="Loves Cats">Loves Cats</SelectItem>
                          <SelectItem value="No Pets">No Pets</SelectItem>
                          <SelectItem value="No Preference">No Preference</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Fitness Level</Label>
                      <Select value={selectedFitness} onValueChange={setSelectedFitness}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="Regular Gym">Regular Gym</SelectItem>
                          <SelectItem value="Yoga">Yoga/Meditation</SelectItem>
                          <SelectItem value="Sports">Active Sports</SelectItem>
                          <SelectItem value="Occasional">Occasional Exercise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Travel Frequency</Label>
                      <Select value={selectedTravelFreq} onValueChange={setSelectedTravelFreq}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="Monthly">Monthly Traveler</SelectItem>
                          <SelectItem value="Quarterly">Quarterly</SelectItem>
                          <SelectItem value="Yearly">Yearly</SelectItem>
                          <SelectItem value="Rarely">Rarely</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Family Background */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="size-5" />
                      Family Values
                    </CardTitle>
                    <CardDescription>Family background preferences</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Family Values</Label>
                      <Select value={selectedFamilyValues} onValueChange={setSelectedFamilyValues}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="Traditional">Traditional</SelectItem>
                          <SelectItem value="Moderate">Moderate</SelectItem>
                          <SelectItem value="Liberal">Liberal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-700">
                        <span className="font-semibold">Tip:</span> Matching family values leads to 85% higher compatibility
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Language Preferences */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="size-5" />
                      Language Proficiency
                    </CardTitle>
                    <CardDescription>Select preferred languages</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      {languages.map((lang) => (
                        <div key={lang} className="flex items-center space-x-2">
                          <Checkbox 
                            id={lang}
                            checked={selectedLanguages.includes(lang)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedLanguages([...selectedLanguages, lang]);
                              } else {
                                setSelectedLanguages(selectedLanguages.filter(l => l !== lang));
                              }
                            }}
                          />
                          <Label htmlFor={lang} className="text-sm">{lang}</Label>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 sticky bottom-0 bg-white p-4 rounded-lg border shadow-lg">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={clearAllFilters}
                >
                  <X className="size-4 mr-2" />
                  Clear All Filters
                </Button>
                <Button 
                  className={`flex-1 ${currentTheme.gradient} text-white`}
                  onClick={handleSearch}
                  disabled={isSearching}
                >
                  {isSearching ? (
                    <>
                      <div className="animate-spin size-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="size-4 mr-2" />
                      Find Matches
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>

            {/* Results Tab */}
            <TabsContent value="results" className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  {results.length} Perfect Matches Found
                </h3>
                <Select defaultValue="matchScore">
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="matchScore">Best Match Score</SelectItem>
                    <SelectItem value="recent">Recently Active</SelectItem>
                    <SelectItem value="response">Highest Response Rate</SelectItem>
                    <SelectItem value="career">Career Stability</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.map((profile) => (
                  <Card key={profile.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <div className="relative">
                      <img 
                        src={profile.image} 
                        alt={profile.name}
                        className="w-full h-64 object-cover"
                      />
                      
                      {/* Match Score Badge */}
                      <Badge className="absolute top-3 left-3 bg-gradient-to-r from-green-500 to-green-600 text-white">
                        <Sparkles className="size-3 mr-1" />
                        {profile.matchScore}% Match
                      </Badge>

                      {profile.premium && (
                        <Badge className="absolute top-3 right-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                          <Star className="size-3 mr-1" />
                          Premium
                        </Badge>
                      )}

                      {/* Activity Indicator */}
                      <div className="absolute bottom-3 left-3">
                        <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm">
                          {profile.lastActive}
                        </Badge>
                      </div>
                    </div>

                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            {profile.name}
                            {profile.verified && (
                              <Check className="size-4 text-blue-600 fill-blue-100" />
                            )}
                            {profile.socialMediaVerified && (
                              <Globe className="size-4 text-green-600" />
                            )}
                          </h3>
                          <p className="text-sm text-gray-600">{profile.age} years, {profile.height}</p>
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Briefcase className="size-4 text-gray-400" />
                          <span>{profile.profession}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <GraduationCap className="size-4 text-gray-400" />
                          <span>{profile.education}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="size-4 text-gray-400" />
                          <span>{profile.location}</span>
                        </div>
                      </div>

                      <Separator />

                      {/* Quick Stats */}
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-purple-50 p-2 rounded">
                          <div className="flex items-center justify-center gap-1">
                            <Camera className="size-3 text-purple-600" />
                            <span className="text-xs font-semibold text-purple-900">{profile.photoCount}</span>
                          </div>
                          <p className="text-xs text-purple-700">Photos</p>
                        </div>
                        <div className="bg-green-50 p-2 rounded">
                          <div className="flex items-center justify-center gap-1">
                            <TrendingUp className="size-3 text-green-600" />
                            <span className="text-xs font-semibold text-green-900">{profile.responseRate}%</span>
                          </div>
                          <p className="text-xs text-green-700">Response</p>
                        </div>
                        <div className="bg-blue-50 p-2 rounded">
                          <div className="flex items-center justify-center gap-1">
                            <Sparkles className="size-3 text-blue-600" />
                            <span className="text-xs font-semibold text-blue-900">{profile.horoscope}</span>
                          </div>
                          <p className="text-xs text-blue-700">Sign</p>
                        </div>
                      </div>

                      <Separator />

                      {/* Interests */}
                      <div className="flex flex-wrap gap-1">
                        {profile.interests.slice(0, 3).map((interest, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {interest}
                          </Badge>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" className="flex-1">
                          View Profile
                        </Button>
                        <Button className={`flex-1 ${currentTheme.gradient} text-white`}>
                          <Heart className="size-4 mr-1" />
                          Connect
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}